import { jsPDF } from "jspdf";
import {
  TEXT_X_MM, TEXT_WIDTH_MM,
  mmToPt, drawHeader, drawFooter, ensureSpace, getTextRegion, getSignatureLineY, drawRichText,
} from "./certificateLayout";
import { renderMarkdown } from "@emr/shared/markdown";
import { fmtDatePdf } from "@emr/shared/india";

// 2026-11-01, Batch 7 Wave 2 dedupe — drawRichText + stripTags were extracted
// verbatim to ./layoutShared.ts (re-exported through this renderer's layout
// sibling, certificateLayout). The copies in referralPdf.ts / patientInfoPdf.ts
// were byte-identical (modulo the export keyword) EXCEPT one drifted constant:
// certificatePdf renders h1 headings at 13pt vs 14pt in the other two — kept
// per call site via the new h1Size parameter (this call site passes 13).

export interface CertificatePdfData {
  recipientType: "whomsoever" | "specific";
  patientName: string | null;
  clinicUhid: string | null;
  patientDob: string | null;
  patientGender: "Male" | "Female" | "Other" | null;
  recipientName: string | null;
  recipientSpecialization: string | null;
  recipientClinic: string | null;
  recipientAddress: string | null;
  recipientGender: "Male" | "Female" | "Other" | null;
  certificateBody: string | null;
  signatoryName: string | null;
  signatoryQualifications: string | null;
  signatoryRegNumber: string | null;
  date: string;
}

export async function createCertificatePdf(data: CertificatePdfData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  await drawHeader(doc);
  await drawFooter(doc);

  const region = getTextRegion(doc);
  let y = region.y;
  const x = region.x;
  const w = region.width;

  // Date (right-aligned)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 95, 88);
  doc.text(fmtDatePdf(data.date), x + w, y, { align: "right" });
  y += mmToPt(7);

  if (data.recipientType === "whomsoever") {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(124, 45, 18);
    const heading = "TO WHOMSOEVER IT MAY CONCERN";
    doc.text(heading, x + w / 2, y, { align: "center" });
    y += mmToPt(8);
  } else {
    // Specific recipient
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(26, 20, 19);
    if (data.recipientName) {
      doc.setFont("helvetica", "bold");
      doc.text(data.recipientName, x, y);
      y += mmToPt(5);
    }
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 50, 45);
    if (data.recipientSpecialization) {
      doc.text(data.recipientSpecialization, x, y);
      y += mmToPt(4.5);
    }
    if (data.recipientClinic) {
      doc.text(data.recipientClinic, x, y);
      y += mmToPt(4.5);
    }
    if (data.recipientAddress) {
      const addrLines = doc.splitTextToSize(data.recipientAddress, w);
      doc.text(addrLines, x, y);
      y += mmToPt(4.5) * addrLines.length;
    }
    y += mmToPt(3);
    // Salutation by recipient gender
    const salutation =
      data.recipientGender === "Female"
        ? "Dear Madam,"
        : data.recipientGender === "Male"
          ? "Dear Sir,"
          : "Dear Sir/Madam,";
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(26, 20, 19);
    y = await ensureSpace(doc, y, mmToPt(6));
    doc.text(salutation, x, y);
    y += mmToPt(7);
  }

  // Body
  if (data.certificateBody) {
    const html = renderMarkdown(data.certificateBody);
    y = await drawRichText(doc, y, x, w, html, 13);
    y += mmToPt(3);
  }

  // Patient info bar (if patient linked)
  if (data.patientName) {
    y = await ensureSpace(doc, y, mmToPt(8));
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60, 50, 45);
    const patientLine = `Patient: ${data.patientName}${data.clinicUhid ? `  ·  UHID: ${data.clinicUhid}` : ""}`;
    doc.text(patientLine, x, y);
    y += mmToPt(5);
  }

  // Sign-off
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  y = await ensureSpace(doc, y, mmToPt(18));
  doc.text("Yours sincerely,", x, y);
  y += mmToPt(14);

  // Signatory block (bottom-right, anchored above the footer)
  const signY = Math.max(y, getSignatureLineY(doc));
  const signX = x + w - mmToPt(60);
  doc.setDrawColor(200, 190, 180);
  doc.line(signX, signY, signX + mmToPt(50), signY);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(26, 20, 19);
  doc.text(data.signatoryName ?? "Doctor", signX + mmToPt(50), signY + mmToPt(4), { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 95, 88);
  if (data.signatoryQualifications) {
    doc.text(data.signatoryQualifications, signX + mmToPt(50), signY + mmToPt(7), { align: "right" });
  }
  if (data.signatoryRegNumber) {
    doc.text(`Reg No: ${data.signatoryRegNumber}`, signX + mmToPt(50), signY + mmToPt(10), { align: "right" });
  }

  return doc;
}
